import styled from '@emotion/styled';
import { useState, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { CircularProgress, Dialog, DialogContent, DialogActions } from '@mui/material';
import { useConsumerT } from '../translations/landingConsumer';
import VideoStoryPlayer from '../components/VideoStoryPlayer';
import ConsumerBackButton from '../components/ConsumerBackButton';
import { useSendBackContext } from '../pages/SendBack/SendBackPageContext';
import AlertSnackbar from '../components/AlertSnackbar';
import { getTutorialVideos } from '../../config/tutorialVideos';
import { useParcelLoader } from '../hooks/useParcelLoader';

export default function SendBackScreenContainer() {
  const t = useConsumerT();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { fetchAlert, alertMessage, dispatch: sendBackDispatch } = useSendBackContext();
  const [isLastVideo, setIsLastVideo] = useState(false);
  const videoPlayerRef = useRef(null);
  const { parcel } = useParcelLoader();

  const isFromSelection = !!searchParams.get('type');
  const parcelType = isFromSelection ? searchParams.get('type') : (parcel?.type || searchParams.get('type'));
  const parcelVersion = isFromSelection ? searchParams.get('version') : (parcel?.version || searchParams.get('version'));
  const returnTo = location.state?.returnTo || (isFromSelection ? '/retour' : '/');
  const finalReturnTo = location.state?.finalReturnTo; // Pour préserver la navigation finale

  function handleAlertClose() {
    sendBackDispatch({ type: 'PAGE_SENDBACK_STATES_UPDATE', payload: { fetchAlert: false, alertMessage: '' } });
  }

  function handleGoBack() {
    // Si on a un finalReturnTo et qu'on retourne à /reception ou /retour, repasser le finalReturnTo
    if (finalReturnTo && (returnTo === '/reception' || returnTo === '/retour')) {
      navigate(returnTo, { state: { returnTo: finalReturnTo } });
    } else {
      navigate(returnTo);
    }
  }

  function handleIndexChange(idx, isLast) {
    setIsLastVideo(isLast);
  }

  function handleNextVideo() {
    videoPlayerRef.current?.goNext();
  }

  const videos = getTutorialVideos(parcelType, parcelVersion, 'retour');

  function handleTutorialClick() {
    const params = new URLSearchParams();
    if (parcelType) params.set('type', parcelType);
    if (parcelVersion) params.set('version', parcelVersion);
    const query = params.toString();
    navigate(`/sendback/tutorial${query ? `?${query}` : ''}`, { state: { returnTo: '/sendback' } });
  }

  // isFromSelection (arrivé depuis /retour) : chargement ou vidéos ou redirect sélection
  if (isFromSelection) {
    if (!parcelType) {
      return (
        <Page>
          <AlertSnackbar open={fetchAlert} message={alertMessage} onClose={handleAlertClose} />
          <TopBar>
            <ConsumerBackButton onBack={handleGoBack} variant="light" label={t('sendback.title')} ariaLabel={t('common.back')} />
          </TopBar>
          <StaticContent>
            <CircularProgress size={32} sx={{ color: '#56A6C3' }} />
          </StaticContent>
        </Page>
      );
    }

    if (videos.length === 0) {
      return <Navigate to="/retour" replace />;
    }

    return (
      <Page>
        <AlertSnackbar open={fetchAlert} message={alertMessage} onClose={handleAlertClose} />
        <TopBar>
          <ConsumerBackButton onBack={handleGoBack} variant="light" label={t('sendback.title')} ariaLabel={t('common.back')} />
        </TopBar>
        <VideoZone>
          <VideoStoryPlayer ref={videoPlayerRef} videos={videos} onIndexChange={handleIndexChange} />
        </VideoZone>
        {isLastVideo ? (
          <CtaButton onClick={handleGoBack}>{t('common.back')}</CtaButton>
        ) : (
          <CtaButton onClick={handleNextVideo}>{t('tuto.cta-next')}</CtaButton>
        )}
      </Page>
    );
  }

  // Mode normal (parcelId présent ou accès direct) : étapes statiques
  return (
    <Page>
      <AlertSnackbar open={fetchAlert} message={alertMessage} onClose={handleAlertClose} />
      <TopBar>
        <ConsumerBackButton onBack={handleGoBack} variant="light" label={t('sendback.title')} ariaLabel={t('common.back')} />
      </TopBar>
      <ScrollArea>
        {[1, 2, 3].map(n => (
          <StepCard key={n}>
            <StepNumber>{n}</StepNumber>
            <StepText>
              <StepTitle>{t(`sendback.step-${n}-title`)}</StepTitle>
              <StepBody>{t(`sendback.step-${n}-body`)}</StepBody>
            </StepText>
          </StepCard>
        ))}
        <VideoTile onClick={handleTutorialClick}>
          <VideoTileIcon>▶</VideoTileIcon>
          <VideoTileText>{t('sendback.more-info-link')}</VideoTileText>
        </VideoTile>
      </ScrollArea>
      <CtaButton onClick={() => navigate('/sendback/map')}>
        {t('tuto.cta-start')}
      </CtaButton>
      <Dialog open={!parcel?.id} PaperProps={{ sx: { borderRadius: 3, p: 1, m: 2 } }}>
        <DialogContent sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
          <NoParcelDialogIcon>📦</NoParcelDialogIcon>
          <NoParcelDialogText>{t('sendback.no-parcel-message')}</NoParcelDialogText>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, pb: 2, px: 2 }}>
          <RescanDialogButton onClick={() => navigate('/scan?actionType=returnOrder')}>
            {t('sendback.no-parcel-cta')}
          </RescanDialogButton>
          <HomeDialogButton onClick={() => navigate('/')}>
            {t('bravo.restart-cta')}
          </HomeDialogButton>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

const Page = styled.div`
  height: 100vh;
  background: ${p => p.theme.palette.background.default};
  font-family: ${p => p.theme.typography.fontFamily};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 12px 8px;
  box-sizing: border-box;
  gap: 6px;
`;

const TopBar = styled.div`
  flex-shrink: 0;
`;

const VideoZone = styled.div`
  flex: 1;
  max-height: calc(100vh - 150px);
  min-height: 0;
  display: flex;
`;

const CtaButton = styled.button`
  width: 100%;
  background: ${p => p.theme.palette.primary.main};
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  font-family: ${p => p.theme.typography.fontFamily};
  cursor: pointer;
  transition: opacity 0.2s;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

const NoParcelDialogIcon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
  line-height: 1;
`;

const NoParcelDialogText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.palette.text.primary};
  line-height: 1.6;
`;

const RescanDialogButton = styled.button`
  width: 100%;
  background: ${p => p.theme.palette.primary.main};
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  font-family: ${p => p.theme.typography.fontFamily};
  cursor: pointer;
  transition: opacity 0.2s;

  &:active { opacity: 0.8; }
`;

const HomeDialogButton = styled.button`
  width: 100%;
  margin-left: 0 !important;
  background: transparent;
  color: ${p => p.theme.palette.text.secondary};
  border: 1px solid ${p => p.theme.palette.border.main};
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 13px;
  font-weight: 500;
  font-family: ${p => p.theme.typography.fontFamily};
  cursor: pointer;
  transition: opacity 0.2s;

  &:active { opacity: 0.8; }
`;

const StaticContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ScrollArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
  width: 100%;
  box-sizing: border-box;
`;

const StepCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: ${p => p.theme.palette.background.paper};
  border: 1px solid ${p => p.theme.palette.border.main};
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: ${p => p.theme.shadows[1]};
`;

const StepNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${p => p.theme.palette.accent.main};
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const StepTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.palette.text.primary};
  line-height: 1.3;
`;

const StepBody = styled.div`
  font-size: 12px;
  color: ${p => p.theme.palette.text.secondary};
  line-height: 1.5;
`;

const VideoTile = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #FFFFFF;
  border: 1px solid ${p => p.theme.palette.accent.main};
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  flex-shrink: 0;
  font-family: ${p => p.theme.typography.fontFamily};

  &:hover {
    opacity: 0.92;
  }

  &:active {
    transform: scale(0.98);
    opacity: 0.85;
  }
`;

const VideoTileIcon = styled.span`
  font-size: 14px;
  line-height: 1;
  color: ${p => p.theme.palette.accent.main};
`;

const VideoTileText = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.theme.palette.accent.main};
  letter-spacing: 0.2px;
`;

